import React from "react";
import { niceNumber } from "@/lib/util";
import { useLingui } from "@lingui/react/macro";

type Props = {
    title: string;
    modelValue: number;
    normativeValue: number;
    unit: string;
    higherIsBetter: boolean;
};

/**
 * Renders a comparison badge for a property (e.g., speed or setup time).
 */
const ComparisonBadge = ({ title, modelValue, normativeValue, unit, higherIsBetter }: Props) => {
    const diff = modelValue - normativeValue;
    const percentage = normativeValue !== 0 ? (diff / normativeValue) * 100 : 0;
    const { t } = useLingui();

    let colorClass = "card-badge-neutral";
    let label = t`Same`;

    if (higherIsBetter) {
        if (diff > 0.001) {
            colorClass = "variant-success";
            const percent = niceNumber(percentage, 2);
            label = t`+${percent}% Faster`;
        } else if (diff < -0.001) {
            colorClass = "variant-danger";
            const percent = niceNumber(percentage, 2);
            label = t`${percent}% Slower`;
        }
    } else {
        if (diff < -0.001) {
            colorClass = "variant-success";
            const percent = niceNumber(Math.abs(percentage), 2);
            label = t`-${percent}% Faster`;
        } else if (diff > 0.001) {
            colorClass = "variant-danger";
            const percent = niceNumber(percentage, 2);
            label = t`+${percent}% Slower`;
        }
    }

    return (
        <div className="card">
            <div className="d-flex justify-content-between gap-3 height-100">
                <div className="card-content-main d-flex flex-column gap-2">
                    <div className="card-label card-label-em">{title}</div>
                    <div className="card-value card-value--big">{niceNumber(modelValue, 2, 2)}</div>
                    <div className="card-label mb-3">
                        {unit} ({t`Model`})
                    </div>
                </div>
                <div className="card-content-side d-flex flex-column justify-content-between align-items-end gap-1">
                    <span className={`card-badge ${colorClass}`}>{label}</span>
                    <div className="card-label">
                        {t`Normative`}: <strong>{niceNumber(normativeValue, 2, 2)}</strong>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ComparisonBadge;
